import React, { useState, useEffect } from 'react';
import './BlogAdmin.css';

const BlogAdmin = ({ onBack }) => {
  const [blogs, setBlogs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Info');
  const [image, setImage] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);

  // Load blogs from localStorage
  useEffect(() => {
    const savedBlogs = localStorage.getItem('stocks4u_blogs');
    if (savedBlogs) {
      setBlogs(JSON.parse(savedBlogs));
    }
  }, []);

  // Save blogs to localStorage
  const saveBlogs = (updatedBlogs) => {
    localStorage.setItem('stocks4u_blogs', JSON.stringify(updatedBlogs));
    setBlogs(updatedBlogs);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!title || !excerpt) {
      alert('Title and excerpt are required!');
      return;
    }

    const blogData = {
      id: isEditing ? editId : Date.now(),
      title,
      excerpt,
      content,
      category,
      image,
      isFeatured,
      date: new Date().toISOString()
    };

    let updatedBlogs;
    if (isEditing) {
      updatedBlogs = blogs.map(blog => 
        blog.id === editId ? blogData : blog
      );
    } else {
      // If new blog is featured, un-feature others
      if (isFeatured) {
        updatedBlogs = blogs.map(blog => ({ ...blog, isFeatured: false }));
        updatedBlogs = [...updatedBlogs, blogData];
      } else {
        updatedBlogs = [...blogs, blogData];
      }
    }

    saveBlogs(updatedBlogs);
    resetForm();
    setShowForm(false);
    alert(isEditing ? 'Blog updated successfully!' : 'Blog posted successfully!');
  };

  const handleEdit = (blog) => {
    setEditId(blog.id);
    setTitle(blog.title);
    setExcerpt(blog.excerpt);
    setContent(blog.content || '');
    setCategory(blog.category || 'Info');
    setImage(blog.image || '');
    setIsFeatured(blog.isFeatured || false);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      const updatedBlogs = blogs.filter(blog => blog.id !== id);
      saveBlogs(updatedBlogs);
    }
  };

  const resetForm = () => {
    setTitle('');
    setExcerpt('');
    setContent('');
    setCategory('Info');
    setImage('');
    setIsFeatured(false);
    setIsEditing(false);
    setEditId(null);
  };

  const handleNewBlog = () => {
    resetForm();
    setShowForm(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="blog-admin-page">
      <header className="blog-admin-header">
        <div className="blog-admin-header-container">
          {onBack && (
            <button className="blog-admin-back-btn" onClick={onBack}>
              ← Back
            </button>
          )}
          <h1>Blog Admin Panel</h1>
          <button className="new-blog-btn" onClick={handleNewBlog}>
            + New Blog
          </button>
        </div>
      </header>

      <main className="blog-admin-main">
        {/* Blog Form */}
        {showForm && (
          <section className="blog-form-section">
            <h2>{isEditing ? 'Edit Blog' : 'Create New Blog'}</h2>
            <form className="blog-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter blog title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="Info">Info</option>
                    <option value="News">News</option>
                    <option value="Update">Update</option>
                    <option value="Announcement">Announcement</option>
                    <option value="Tutorial">Tutorial</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Excerpt *</label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Short description (shown in blog cards)"
                  rows="2"
                  required
                />
              </div>

              <div className="form-group">
                <label>Full Content</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Full blog content (optional)"
                  rows="6"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="file-input"
                  />
                  {image && (
                    <div className="image-preview">
                      <img src={image} alt="Preview" />
                      <button 
                        type="button" 
                        className="remove-image-btn"
                        onClick={() => setImage('')}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isFeatured}
                      onChange={(e) => setIsFeatured(e.target.checked)}
                    />
                    <span>Feature this blog (shown in hero section)</span>
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {isEditing ? 'Update Blog' : 'Post Blog'}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* Blog List */}
        <section className="blog-list-section">
          <h2>All Blogs ({blogs.length})</h2>
          {blogs.length === 0 ? (
            <div className="empty-state">
              <p>No blogs yet. Click "New Blog" to create one!</p>
            </div>
          ) : (
            <div className="admin-blog-list">
              {blogs.map(blog => (
                <div key={blog.id} className={`admin-blog-card ${blog.isFeatured ? 'featured' : ''}`}>
                  <div className="admin-blog-info">
                    {blog.image && (
                      <div className="admin-blog-thumb">
                        <img src={blog.image} alt={blog.title} />
                      </div>
                    )}
                    <div className="admin-blog-details">
                      <h3>{blog.title}</h3>
                      <p>{blog.excerpt}</p>
                      <div className="admin-blog-meta">
                        <span className="admin-blog-category">{blog.category}</span>
                        <span className="admin-blog-date">{formatDate(blog.date)}</span>
                        {blog.isFeatured && <span className="featured-badge">★ Featured</span>}
                      </div>
                    </div>
                  </div>
                  <div className="admin-blog-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEdit(blog)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(blog.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default BlogAdmin;
